import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine, WarningLine} from '@dropbox/dig-icons/assets';
import cx from 'classnames';
import {RoadmapNodeRole, type RoadmapRollupResponse, type RoadmapRollupRow} from 'client';
import {Link} from 'components/DSYS/Link';
import type {Column} from 'components/DSYS/Table';
import {Table} from 'components/DSYS/Table';
import {forwardRef, type HTMLAttributes, useEffect, useMemo, useState} from 'react';
import styles from 'views/roadmap/Roadmap.module.css';
import {RoadmapInitiativeLink} from 'views/roadmap/RoadmapInitiativeLink';

const ROLLUP_COLUMNS: Column<RoadmapRollupRow>[] = [
  {type: 'hierarchy', minWidth: 300, spanner: true, cantDisable: true},
  {
    type: 'initiatives',
    fixed: true,
    cantDisable: true,
    tooltip: 'Roadmap Initiatives, with the estimated subset shown below.',
  },
  {type: 'commitment', fixed: true, cantDisable: true},
  {
    type: 'dependencies',
    fixed: true,
    cantDisable: true,
    tooltip: 'Unique classified issue-link relationships for descendant initiatives.',
  },
  {type: 'epics', fixed: true, cantDisable: true},
  {type: 'dev_weeks', fixed: true, cantDisable: true},
  {type: 'headcount', fixed: true, cantDisable: true},
  {
    type: 'quarter_share',
    fixed: true,
    cantDisable: true,
    tooltip: 'Share of developer weeks across the selected quarter and timeframe.',
  },
  {
    type: 'scope_share',
    fixed: true,
    cantDisable: true,
    tooltip: 'Share of developer weeks within this scope root.',
  },
];

const EMPTY_ROLLUP_ROWS: RoadmapRollupRow[] = [];
const JIRA_SEARCH_URL = 'https://dropbox.atlassian.net/issues/';

const getQuarterDisplayLabel = (quarter: string): string => {
  const match = quarter.trim().match(/q([1-4])$/i);
  return match ? `Q${match[1]}` : quarter;
};

type RollupJiraQueryType = 'initiatives' | 'dependencies' | 'epics';

export const buildRoadmapRollupJiraUrl = (
  queryType: RollupJiraQueryType,
  initiativeKeys: string[]
): string | null => {
  const uniqueKeys = [...new Set(initiativeKeys)].sort();
  if (uniqueKeys.length === 0) return null;

  const keys = uniqueKeys.map((key) => `"${key.replaceAll('"', '\\"')}"`).join(', ');
  const jql = {
    initiatives: `key in (${keys}) ORDER BY key ASC`,
    dependencies: `workItemLink in (${keys}) AND workItemLinkType in ("Blocks", "Dependency") ORDER BY key ASC`,
    epics: `parent in (${keys}) AND type = Epic ORDER BY key ASC`,
  }[queryType];

  const url = new URL(JIRA_SEARCH_URL);
  url.searchParams.set('jql', jql);
  return url.toString();
};

export const formatRoadmapRollupNumber = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {maximumFractionDigits: 2}).format(value);
};

export const formatRoadmapRollupPercentage = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value);
};

const RollupSummaryMetric = ({
  label,
  devWeeks,
  headcount,
}: {
  label: string;
  devWeeks: number;
  headcount: number;
}) => (
  <Tooltip title={`Total HC: ${formatRoadmapRollupNumber(headcount)}`} placement="bottom">
    <div className={styles.rollupSummaryMetric}>
      <Text size="small" color="subtle">
        {label}
      </Text>
      <Text size="large" isBold>
        {formatRoadmapRollupNumber(devWeeks)} dev weeks
      </Text>
    </div>
  </Tooltip>
);

const RollupMetricValue = forwardRef<
  HTMLDivElement,
  {value: string} & HTMLAttributes<HTMLDivElement>
>(({value, className, ...props}, ref) => (
  <div {...props} ref={ref} className={cx(styles.rollupMetric, className)}>
    <Text size="small">{value}</Text>
  </div>
));
RollupMetricValue.displayName = 'RollupMetricValue';

const RollupMetric = ({
  value,
  detail,
  href,
  linkLabel,
}: {
  value: string;
  detail?: string;
  href?: string | null;
  linkLabel?: string;
}) => {
  const valueContent =
    href && linkLabel ? (
      <Link to={href} aria-label={linkLabel} hasNoUnderline monochromatic showUnderlineOnHover>
        <RollupMetricValue value={value} />
      </Link>
    ) : (
      <RollupMetricValue value={value} />
    );

  return detail ? (
    <Tooltip title={detail} placement="top">
      {valueContent}
    </Tooltip>
  ) : (
    valueContent
  );
};

const RollupRow = ({
  row,
  initiativeKeys,
  minimumDepth,
  hasChildren,
  isExpanded,
  onToggle,
}: {
  row: RoadmapRollupRow;
  initiativeKeys: string[];
  minimumDepth: number;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const {node, metrics} = row;
  const {structural, estimates, percentages} = metrics;
  const indent = Math.max(0, node.depth - minimumDepth) * 20;
  const totalHeadcount = (estimates.fte_headcount ?? 0) + (estimates.cw_headcount ?? 0);
  const hasNoEstimates =
    (structural.initiative_count ?? 0) > 0 && (structural.estimated_initiative_count ?? 0) === 0;
  const initiativesJiraUrl = buildRoadmapRollupJiraUrl('initiatives', initiativeKeys);
  const dependenciesJiraUrl = buildRoadmapRollupJiraUrl('dependencies', initiativeKeys);
  const epicsJiraUrl = buildRoadmapRollupJiraUrl('epics', initiativeKeys);

  return (
    <Table.Row className={cx({[styles.rollupScopeRow]: node.depth === minimumDepth})}>
      <Table.Cell>
        <div className={styles.rollupHierarchyCell} style={{paddingLeft: indent}}>
          {hasChildren ? (
            <button
              type="button"
              className={styles.rollupToggle}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.title}`}
              aria-expanded={isExpanded}
              onClick={onToggle}
            >
              <UIIcon src={isExpanded ? ChevronDownLine : ChevronRightLine} size="small" />
            </button>
          ) : (
            <span className={styles.rollupToggleSpacer} />
          )}
          <RoadmapInitiativeLink
            title={node.title}
            jiraKey={node.key}
            lines={1}
            isBold={hasChildren}
            className={styles.rollupHierarchyLabel}
          />
        </div>
      </Table.Cell>
      <Table.Cell>
        <RollupMetric
          value={formatRoadmapRollupNumber(structural.initiative_count)}
          detail={`${formatRoadmapRollupNumber(structural.estimated_initiative_count)} estimated`}
          href={(structural.initiative_count ?? 0) > 0 ? initiativesJiraUrl : null}
          linkLabel={`View initiatives for ${node.title} in Jira`}
        />
      </Table.Cell>
      <Table.Cell>
        <RollupMetric
          value={formatRoadmapRollupNumber(structural.committed_count)}
          detail={`${formatRoadmapRollupNumber(structural.not_committed_count)} not committed · ${formatRoadmapRollupNumber(structural.stretch_count)} stretch · ${formatRoadmapRollupNumber(structural.cancelled_count)} cancelled`}
        />
      </Table.Cell>
      <Table.Cell>
        <RollupMetric
          value={formatRoadmapRollupNumber(structural.dependency_count)}
          href={(structural.dependency_count ?? 0) > 0 ? dependenciesJiraUrl : null}
          linkLabel={`View linked dependencies for ${node.title} in Jira`}
        />
      </Table.Cell>
      <Table.Cell>
        <RollupMetric
          value={formatRoadmapRollupNumber(structural.epic_count)}
          href={(structural.epic_count ?? 0) > 0 ? epicsJiraUrl : null}
          linkLabel={`View epics for ${node.title} in Jira`}
        />
      </Table.Cell>
      <Table.Cell>
        <RollupMetric
          value={
            hasNoEstimates ? 'No Estimate' : formatRoadmapRollupNumber(estimates.total_dev_weeks)
          }
          detail={
            hasNoEstimates
              ? undefined
              : `FTE ${formatRoadmapRollupNumber(estimates.fte_dev_weeks)} · CW ${formatRoadmapRollupNumber(estimates.cw_dev_weeks)}`
          }
        />
      </Table.Cell>
      <Table.Cell>
        <RollupMetric
          value={hasNoEstimates ? 'No Estimate' : formatRoadmapRollupNumber(totalHeadcount)}
          detail={
            hasNoEstimates
              ? undefined
              : `FTE ${formatRoadmapRollupNumber(estimates.fte_headcount)} · CW ${formatRoadmapRollupNumber(estimates.cw_headcount)}`
          }
        />
      </Table.Cell>
      <Table.Cell>
        <RollupMetric
          value={formatRoadmapRollupPercentage(percentages.percentage_of_selected_quarter_estimate)}
        />
      </Table.Cell>
      <Table.Cell>
        <RollupMetric value={formatRoadmapRollupPercentage(percentages.percentage_of_scope)} />
      </Table.Cell>
    </Table.Row>
  );
};

export const RollupTab = ({
  rollup,
  scopeLabel,
  summaryLabel = 'Company',
}: {
  rollup: RoadmapRollupResponse;
  scopeLabel?: string | null;
  summaryLabel?: string;
}) => {
  const rows = rollup.rows ?? EMPTY_ROLLUP_ROWS;
  const quarterLabel = getQuarterDisplayLabel(rollup.selected_quarter);
  const scopeTitle = scopeLabel ?? rollup.selected_scope?.title ?? 'Scope';
  const selectedScopeEstimates = rollup.selected_scope
    ? rows.find((row) => row.node.key === rollup.selected_scope?.key)?.metrics.estimates
    : null;
  const selectedScopeTotalDevWeeks = selectedScopeEstimates?.total_dev_weeks;
  const selectedScopeTotalHeadcount = selectedScopeEstimates
    ? (selectedScopeEstimates.fte_headcount ?? 0) + (selectedScopeEstimates.cw_headcount ?? 0)
    : null;
  const rowKeys = useMemo(() => new Set(rows.map((row) => row.node.key)), [rows]);
  const parentKeys = useMemo(
    () =>
      new Set(
        rows
          .map((row) => row.node.parent_key)
          .filter((key): key is string => Boolean(key && rowKeys.has(key)))
      ),
    [rowKeys, rows]
  );
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set(parentKeys));
  const initiativeKeysByNodeKey = useMemo(() => {
    const byNodeKey = new Map(rows.map((row) => [row.node.key, [] as string[]]));
    rows
      .filter((row) => row.node.semantic_role === RoadmapNodeRole.ROADMAP_INITIATIVE)
      .forEach((initiative) => {
        [initiative.node.key, ...(initiative.node.ancestor_keys ?? [])].forEach((nodeKey) => {
          byNodeKey.get(nodeKey)?.push(initiative.node.key);
        });
      });
    return byNodeKey;
  }, [rows]);

  useEffect(() => {
    setExpandedKeys(new Set(parentKeys));
  }, [parentKeys]);

  const minimumDepth = rows.reduce(
    (minimum, row) => Math.min(minimum, row.node.depth),
    rows[0]?.node.depth ?? 0
  );
  const visibleRows = rows.filter((row) =>
    (row.node.ancestor_keys ?? [])
      .filter((key) => rowKeys.has(key))
      .every((ancestorKey) => expandedKeys.has(ancestorKey))
  );
  const missingQuarterCount = rollup.diagnostics.missing_planning_quarter_keys?.length ?? 0;

  return (
    <section className={styles.section}>
      <div className={styles.rollupSummary}>
        <RollupSummaryMetric
          label={`${summaryLabel} ${quarterLabel} total`}
          devWeeks={rollup.diagnostics.selected_quarter_total_dev_weeks}
          headcount={rollup.diagnostics.selected_quarter_total_headcount}
        />
        {selectedScopeTotalDevWeeks != null && selectedScopeTotalHeadcount != null ? (
          <RollupSummaryMetric
            label={`${scopeTitle} ${quarterLabel} total`}
            devWeeks={selectedScopeTotalDevWeeks}
            headcount={selectedScopeTotalHeadcount}
          />
        ) : null}
        {missingQuarterCount > 0 ? (
          <Text size="small" color="subtle" className={styles.rollupDiagnostic}>
            <UIIcon src={WarningLine} size="small" />
            {missingQuarterCount} initiative{missingQuarterCount === 1 ? '' : 's'} missing a
            planning quarter
          </Text>
        ) : null}
        {rollup.freshness.is_stale ? (
          <Text size="small" color="subtle" className={styles.rollupDiagnostic}>
            <UIIcon src={WarningLine} size="small" />
            Snapshot is stale
          </Text>
        ) : null}
      </div>

      <div
        className={styles.stickyTableViewport}
        role="region"
        aria-label="Roadmap rollup table"
        tabIndex={0}
      >
        <Table<RoadmapRollupRow>
          columns={ROLLUP_COLUMNS}
          data={visibleRows}
          border={false}
          resizable={false}
          headerTextSize="small"
          columnLabelOverrides={{
            hierarchy: 'Hierarchy',
            initiatives: 'Initiatives',
            commitment: 'Commitment',
            dependencies: 'Linked dependencies',
            epics: 'Epics',
            dev_weeks: 'Dev Weeks',
            headcount: 'Headcount',
            quarter_share: 'Quarter %',
            scope_share: 'Scope %',
          }}
          className={cx(styles.table, styles.rollupTable, styles.stickyTable)}
        >
          <Table.Header data-testid="roadmap-rollup-table-header" />
          <Table.Body className={styles.tableBody}>
            {visibleRows.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={ROLLUP_COLUMNS.length}>
                  <Box padding="24" display="flex" justifyContent="center">
                    <Text color="subtle">
                      No rollup data is available for the selected filters.
                    </Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            ) : (
              visibleRows.map((row) => (
                <RollupRow
                  key={row.node.key}
                  row={row}
                  initiativeKeys={initiativeKeysByNodeKey.get(row.node.key) ?? []}
                  minimumDepth={minimumDepth}
                  hasChildren={parentKeys.has(row.node.key)}
                  isExpanded={expandedKeys.has(row.node.key)}
                  onToggle={() =>
                    setExpandedKeys((current) => {
                      const next = new Set(current);
                      if (next.has(row.node.key)) {
                        next.delete(row.node.key);
                      } else {
                        next.add(row.node.key);
                      }
                      return next;
                    })
                  }
                />
              ))
            )}
          </Table.Body>
        </Table>
      </div>
    </section>
  );
};
